/**
 * Theme Mode Hook
 *
 * Manages light/dark theme preference with per-user persistence in localStorage.
 * Falls back to the OS/browser preference (prefers-color-scheme) when the user
 * has not explicitly chosen a mode.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../auth';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY_PREFIX = 'enterprise-search:theme-mode';

/** Build a per-user storage key, or a generic one when no user is known. */
function storageKey(userId: string | undefined): string {
  return userId ? `${STORAGE_KEY_PREFIX}:${userId}` : STORAGE_KEY_PREFIX;
}

/** Read the OS / browser color-scheme preference. */
function getSystemPreference(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Read the persisted preference from localStorage (if any). */
function loadStoredMode(userId: string | undefined): ThemeMode | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw === 'light' || raw === 'dark') return raw;
  } catch {
    // localStorage unavailable — ignore
  }
  return null;
}

/** Persist the chosen mode to localStorage. */
function saveMode(userId: string | undefined, mode: ThemeMode): void {
  try {
    localStorage.setItem(storageKey(userId), mode);
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * Provides the current theme mode and a toggle function.
 *
 * Resolution order:
 * 1. User-specific value in localStorage
 * 2. OS / browser `prefers-color-scheme`
 */
export function useThemeMode() {
  const { user } = useAuth();
  const userId = user?.id;

  const [mode, setMode] = useState<ThemeMode>(() => {
    return loadStoredMode(userId) ?? getSystemPreference();
  });

  // When the authenticated user changes, reload their preference.
  useEffect(() => {
    const stored = loadStoredMode(userId);
    if (stored) {
      setMode(stored);
    }
    // If no stored preference for this user, keep current mode (which may be
    // the OS default or a previously toggled value).
  }, [userId]);

  // Listen for OS-level theme changes so the app reacts in real time when the
  // user hasn't explicitly chosen a mode.
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');

    const handler = (e: MediaQueryListEvent) => {
      // Only follow OS changes when the user hasn't stored a preference.
      if (!loadStoredMode(userId)) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [userId]);

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next: ThemeMode = prev === 'light' ? 'dark' : 'light';
      saveMode(userId, next);
      return next;
    });
  }, [userId]);

  return useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);
}
